import { useUser } from "lib/user";
import { useMemo } from "react";
import type {
  IncludedDiscountType,
  RejectedDiscountType,
  UserNotificationType
} from "@shared/graphql.types";
import { useNotifications } from "@shared/utils.notifications";

const hasRejectedFriendDiscount = (
  discounts: RejectedDiscountType[]
): boolean =>
  discounts.some(
    (discount) => discount.state === "ReferralRejectedByCondition"
  );

const hasAcceptedAdvocateDiscount = (
  discounts: IncludedDiscountType[]
): boolean =>
  discounts.some(
    (discount) =>
      discount.state === "Accepted" && discount.name.includes("RAF advocate")
  );

const hasAcceptedFriendDiscount = (
  discounts: IncludedDiscountType[]
): boolean =>
  discounts.some(
    (discount) => discount.state === "Accepted" && discount.referralCode
  );

const hasDiscountAvailable = (
  notifications: UserNotificationType[] | null
): boolean =>
  notifications?.some(
    (notification) => notification.code === "RAF_ADVOCATE_DISCOUNT_AVAILABLE"
  ) ?? false;

export type ReferralRoleType = "friend" | "advocate" | null;

export const useReferralRole = (): ReferralRoleType => {
  const { includedDiscounts, rejectedDiscounts } = useUser();
  const { rafNotifications } = useNotifications();

  const role: ReferralRoleType = useMemo(() => {
    if (
      hasAcceptedAdvocateDiscount(includedDiscounts) ||
      hasDiscountAvailable(rafNotifications)
    ) {
      return "advocate";
    }
    if (
      hasAcceptedFriendDiscount(includedDiscounts) ||
      hasRejectedFriendDiscount(rejectedDiscounts)
    ) {
      return "friend";
    }
    return null;
  }, [includedDiscounts, rejectedDiscounts, rafNotifications]);

  return role;
};
